import { RefObject, useLayoutEffect, useRef, useState } from "react";

/**
 * Measures an element's width with a ResizeObserver and maps it through
 * `compute`. Unlike viewport breakpoints (react-grid-system, theme media
 * queries), this reacts to the space the element actually has — e.g. when
 * the right panel squeezes the page without the viewport changing.
 *
 * `T` is restricted to primitives so a resize only re-renders the consumer
 * when the computed value changes (setState bails on Object.is-equal), not
 * on every pixel of a resize or panel animation.
 *
 * Returns `initial` until the first measurement, which runs in a layout
 * effect (before paint), so the measured layout renders without a flash of
 * the initial one.
 */
const useContainerQuery = <T extends string | number | boolean | null>(
  ref: RefObject<HTMLElement | null>,
  compute: (width: number) => T,
  initial: T,
): T => {
  const [value, setValue] = useState<T>(initial);
  const computeRef = useRef(compute);
  computeRef.current = compute;

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const update = () => setValue(computeRef.current(node.clientWidth));

    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);

  return value;
};

export default useContainerQuery;
